import { useEffect, useState, useRef, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Section, Eyebrow, H2, Lead, GlassCard } from "../components/site/Primitives";
import api from "../lib/api";
import { log } from "../lib/logger";
import { CheckCircle2, XCircle, Loader2, ArrowRight } from "lucide-react";

const MAX_POLL_ATTEMPTS = 8;
const POLL_INTERVAL_MS = 2000;

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const kind = params.get("kind") || "payment";
  const [state, setState] = useState({ status: "checking", payment_status: null, amount_total: 0, currency: "usd" });
  const [attempts, setAttempts] = useState(0);
  const cancelledRef = useRef(false);

  const pollOnce = useCallback(async (n) => {
    if (cancelledRef.current) return;
    try {
      const { data } = await api.get(`/payments/status/${sessionId}`);
      if (cancelledRef.current) return;
      setState(data);
      setAttempts(n);
      const settled = data.payment_status === "paid" || data.status === "expired";
      if (settled || n >= MAX_POLL_ATTEMPTS) return;
      setTimeout(() => pollOnce(n + 1), POLL_INTERVAL_MS);
    } catch (err) {
      log.warn("payment status poll failed", err?.message);
      if (cancelledRef.current) return;
      if (n >= MAX_POLL_ATTEMPTS) { setState({ status: "error", payment_status: "error" }); return; }
      setTimeout(() => pollOnce(n + 1), POLL_INTERVAL_MS);
    }
  }, [sessionId]);

  useEffect(() => {
    cancelledRef.current = false;
    if (!sessionId) { setState({ status: "expired", payment_status: "missing" }); return; }
    pollOnce(0);
    return () => { cancelledRef.current = true; };
  }, [sessionId, pollOnce]);

  const paid = state.payment_status === "paid";
  const expired = state.status === "expired" || attempts >= MAX_POLL_ATTEMPTS;
  const checking = !paid && !expired;
  const amount = (state.amount_total || 0) / 100;

  return (
    <Section className="pt-40 max-w-2xl" >
      <GlassCard className="p-10 md:p-14" data-testid="payment-success">
        {checking && <CheckingState kind={kind} />}
        {paid && <PaidState kind={kind} amount={amount} currency={state.currency} />}
        {expired && <ExpiredState />}
      </GlassCard>
    </Section>
  );
}

function CheckingState({ kind }) {
  return (
    <>
      <Loader2 className="text-gold animate-spin mb-6" size={40} />
      <Eyebrow>Processing</Eyebrow>
      <H2>Confirming your {kind}…</H2>
      <Lead className="mt-4">Hold tight — this usually takes a few seconds.</Lead>
    </>
  );
}

function PaidState({ kind, amount, currency }) {
  return (
    <>
      <CheckCircle2 className="text-gold mb-6" size={48} />
      <Eyebrow>Payment received</Eyebrow>
      <H2>Thank you.</H2>
      <Lead className="mt-4">
        Your <strong>${amount.toLocaleString(undefined, { minimumFractionDigits: 2 })} {currency?.toUpperCase()}</strong> {kind} has been confirmed.
        A receipt is on its way to your inbox.
      </Lead>
      <div className="mt-8 flex flex-wrap gap-4">
        {kind === "donation"
          ? <Link to="/veteran-housing" className="btn-primary">Return to Veteran Housing <ArrowRight size={14} /></Link>
          : <Link to="/portal/tenant" className="btn-primary">Back to Tenant Portal <ArrowRight size={14} /></Link>}
        <Link to="/" className="btn-ghost">Home</Link>
      </div>
    </>
  );
}

function ExpiredState() {
  return (
    <>
      <XCircle className="text-red-400 mb-6" size={40} />
      <Eyebrow>Couldn't confirm</Eyebrow>
      <H2>We couldn't confirm this payment.</H2>
      <Lead className="mt-4">The payment may still be processing. Check your email for a confirmation, or try again.</Lead>
      <div className="mt-8 flex flex-wrap gap-4">
        <Link to="/veteran-housing" className="btn-ghost">Try again</Link>
        <Link to="/" className="btn-ghost">Home</Link>
      </div>
    </>
  );
}

export function PaymentCancel() {
  return (
    <Section className="pt-40 max-w-2xl">
      <GlassCard className="p-10 md:p-14">
        <Eyebrow>Cancelled</Eyebrow>
        <H2>No charge was made.</H2>
        <Lead className="mt-4">Your checkout was cancelled. You can try again any time.</Lead>
        <div className="mt-8"><Link to="/" className="btn-primary">Return Home <ArrowRight size={14} /></Link></div>
      </GlassCard>
    </Section>
  );
}
